import { API_URL } from '../utils/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, Pencil, Trash2, Calendar, Search, Wrench, Truck, CheckCircle2,
  Clock, XCircle, ChevronLeft, ChevronRight, Phone, MapPin, Package,
  Camera, X, Eye, AlertTriangle, FileText, Download, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '../lib/utils';


const STATUS_CONFIG = {
  bekliyor: { label: 'Kurulum Bekliyor', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
  planlandi: { label: 'Planlama Yapıldı', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: Calendar },
  devam_ediyor: { label: 'Kurulum Devam Ediyor', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', icon: Wrench },
  tamamlandi: { label: 'Kurulum Tamamlandı', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle2 },
  iptal: { label: 'İptal Edildi', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle }
};

const TechnicalService = () => {
  const { user, token } = useAuth();
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all'); // all, weekly, monthly, yearly
  const [viewMode, setViewMode] = useState('list'); // calendar, list
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_address: '',
    customer_city: '',
    customer_district: '',
    sale_amount: '',
    items: [],
    planned_date: '',
    planned_time: '',
    status: 'bekliyor',
    notes: '',
    internal_notes: '',
    description: '',
    is_repair: false,
    repair_description: '',
    faulty_products: ''
  });

  const canManage = user?.permissions?.includes('all') || user?.permissions?.includes('technical_service_manage');
  const canView = user?.permissions?.includes('all') || user?.permissions?.includes('technical_service_view') || canManage;

  const headers = { Authorization: `Bearer ${token}` };

  // Verileri yükle
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;

      const [servicesRes, calendarRes, statsRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/api/technical-services`, { headers }),
        axios.get(`${API_URL}/api/technical-services/calendar?year=${year}&month=${month}`, { headers }),
        axios.get(`${API_URL}/api/technical-services/stats/summary`, { headers }),
        axios.get(`${API_URL}/api/products`, { headers })
      ]);

      setServices(servicesRes.data);
      setCalendarData(calendarRes.data);
      setStats(statsRes.data);
      setProducts(productsRes.data || []);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [token, currentMonth]);

  useEffect(() => {
    if (canView) {
      fetchData();
    }
  }, [canView, fetchData]);

  // Takvim navigasyonu
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  // Takvim günlerini oluştur
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = (firstDay.getDay() + 6) % 7;
    const days = [];

    for (let i = 0; i < startPadding; i++) {
      days.push({ day: null, isCurrentMonth: false });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      const dayServices = calendarData[dateStr] || [];
      days.push({
        day: i,
        dateStr,
        isCurrentMonth: true,
        isToday: dateStr === new Date().toISOString().split('T')[0],
        services: dayServices,
        hasServices: dayServices.length > 0
      });
    }

    return days;
  };

  // Tarih seçimi
  const handleDateClick = (dateStr, dayServices) => {
    setSelectedDate(dateStr);
    if (dayServices && dayServices.length > 0) {
      // Günün servislerini göster
    } else if (canManage) {
      resetForm();
      setFormData(prev => ({ ...prev, planned_date: dateStr }));
      setIsModalOpen(true);
    }
  };

  // Dönem filtreleme
  const filterByPeriod = (serviceList) => {
    if (periodFilter === 'all') return serviceList;
    
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const endOfYear = new Date(now.getFullYear(), 11, 31);

    return serviceList.filter(s => {
      if (!s.planned_date) return false;
      const serviceDate = new Date(s.planned_date);
      
      switch (periodFilter) {
        case 'weekly':
          return serviceDate >= startOfWeek && serviceDate <= endOfWeek;
        case 'monthly':
          return serviceDate >= startOfMonth && serviceDate <= endOfMonth;
        case 'yearly':
          return serviceDate >= startOfYear && serviceDate <= endOfYear;
        default:
          return true;
      }
    });
  };

  // Form sıfırla
  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      customer_address: '',
      customer_city: '',
      customer_district: '',
      sale_amount: '',
      items: [],
      planned_date: '',
      planned_time: '',
      status: 'bekliyor',
      notes: '',
      internal_notes: '',
      description: '',
      is_repair: false,
      repair_description: '',
      faulty_products: ''
    });
    setProductSearchTerm('');
    setEditingService(null);
  };

  // Modal aç
  const openModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        customer_name: service.customer_name || '',
        customer_phone: service.customer_phone || '',
        customer_address: service.customer_address || '',
        customer_city: service.customer_city || '',
        customer_district: service.customer_district || '',
        sale_amount: service.sale_amount || '',
        items: service.items || [],
        planned_date: service.planned_date || '',
        planned_time: service.planned_time || '',
        status: service.status || 'bekliyor',
        notes: service.notes || '',
        internal_notes: service.internal_notes || '',
        description: service.description || '',
        is_repair: service.is_repair || false,
        repair_description: service.repair_description || '',
        faulty_products: service.faulty_products || ''
      });
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  // Detay modal
  const openDetailModal = (service) => {
    setSelectedService(service);
    setIsDetailModalOpen(true);
  };

  // Ürün listesinden ürün ekle
  const addProductFromList = (product) => {
    const existingIndex = formData.items.findIndex(i => i.product_id === product.id);
    if (existingIndex >= 0) {
      // Zaten varsa miktarı artır
      const newItems = [...formData.items];
      newItems[existingIndex].quantity += 1;
      newItems[existingIndex].total_price = newItems[existingIndex].quantity * newItems[existingIndex].unit_price;
      setFormData(prev => ({ ...prev, items: newItems }));
    } else {
      // Yeni ürün ekle
      const item = {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.sale_price_tl || product.price_tl || 0,
        total_price: product.sale_price_tl || product.price_tl || 0
      };
      setFormData(prev => ({ ...prev, items: [...prev.items, item] }));
    }
    setProductSearchTerm('');
    toast.success('Ürün eklendi');
  };

  // Ürün miktarını güncelle
  const updateItemQuantity = (index, newQuantity) => {
    const newItems = [...formData.items];
    newItems[index].quantity = Math.max(1, parseInt(newQuantity) || 1);
    newItems[index].total_price = newItems[index].quantity * newItems[index].unit_price;
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  // Ürün sil
  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Kaydet
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name.trim()) {
      toast.error('Müşteri adı zorunludur');
      return;
    }
    if (!formData.customer_phone.trim()) {
      toast.error('Telefon numarası zorunludur');
      return;
    }

    try {
      const payload = {
        ...formData,
        sale_amount: parseFloat(formData.sale_amount) || 0,
        source_type: 'manual'
      };

      if (editingService) {
        await axios.put(`${API_URL}/api/technical-services/${editingService.id}`, payload, { headers });
        toast.success('Kayıt güncellendi');
      } else {
        await axios.post(`${API_URL}/api/technical-services`, payload, { headers });
        toast.success('Yeni kayıt oluşturuldu');
      }

      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      toast.error(error.response?.data?.detail || 'Kaydetme hatası');
    }
  };

  // Durum güncelle
  const updateStatus = async (serviceId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/technical-services/${serviceId}/status`, 
        { status: newStatus }, 
        { headers }
      );
      toast.success('Durum güncellendi');
      fetchData();
      if (selectedService?.id === serviceId) {
        setSelectedService(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      toast.error('Durum güncellenemedi');
    }
  };

  // Sil
  const handleDelete = async (serviceId) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/technical-services/${serviceId}`, { headers });
      toast.success('Kayıt silindi');
      setIsDetailModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Silme hatası:', error);
      toast.error('Silme hatası');
    }
  };

  // Fotoğraf yükle
  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !selectedService) return;

    setUploadingPhotos(true);
    const formDataUpload = new FormData();
    for (let i = 0; i < files.length; i++) {
      formDataUpload.append('files', files[i]);
    }

    try {
      const res = await axios.post(
        `${API_URL}/api/technical-services/${selectedService.id}/upload-photos`,
        formDataUpload,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`${res.data.uploaded_urls.length} fotoğraf yüklendi`);
      
      const updated = await axios.get(`${API_URL}/api/technical-services/${selectedService.id}`, { headers });
      setSelectedService(updated.data);
      fetchData();
    } catch (error) {
      console.error('Fotoğraf yükleme hatası:', error);
      toast.error('Fotoğraf yüklenemedi');
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Fotoğraf sil
  const handlePhotoDelete = async (photoIndex) => {
    if (!selectedService) return;
    
    try {
      await axios.delete(`${API_URL}/api/technical-services/${selectedService.id}/photos/${photoIndex}`, { headers });
      toast.success('Fotoğraf silindi');
      
      const updated = await axios.get(`${API_URL}/api/technical-services/${selectedService.id}`, { headers });
      setSelectedService(updated.data);
      fetchData();
    } catch (error) {
      console.error('Fotoğraf silme hatası:', error);
      toast.error('Fotoğraf silinemedi');
    }
  };

  // Ürün listesi PDF oluştur (Backend API)
  const generateProductListPdf = async (service) => {
    try {
      toast.info('PDF oluşturuluyor...');
      const response = await axios.get(
        `${API_URL}/api/technical-services/${service.id}/product-list-pdf`,
        { 
          headers,
          responseType: 'blob'
        }
      );
      
      // Blob'u indir
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `urun_listesi_${service.customer_name?.replace(/\s+/g, '_') || 'musteri'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF indirildi');
    } catch (error) {
      console.error('PDF oluşturma hatası:', error);
      toast.error('PDF oluşturulamadı');
    }
  };

  // Filtreleme
  const filteredServices = filterByPeriod(services.filter(s => {
    const matchSearch = s.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       s.customer_phone?.includes(searchTerm);
    const matchStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchSearch && matchStatus;
  }));

  // Para formatı
  const formatCurrency = (value) => {
    return `₺${(value || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
  };

  // Tarih formatı
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  // Durum badge
  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.bekliyor;
    const Icon = config.icon;
    return (
      <Badge className={cn('flex items-center gap-1', config.color)}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  // Ürün arama sonuçları
  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(productSearchTerm.toLowerCase())
  ).slice(0, 10);

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Bu sayfayı görüntüleme yetkiniz yok.</p>
      </div>
    );
  }

  const calendarDays = generateCalendarDays();
  const weekDays = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

  // Gelecek kurulumlar (tamamlanmamış ve iptal edilmemiş)
  const upcomingServices = filteredServices
    .filter(s => s.status !== 'tamamlandi' && s.status !== 'iptal')
    .sort((a, b) => {
      if (!a.planned_date) return 1;
      if (!b.planned_date) return -1;
      return new Date(a.planned_date) - new Date(b.planned_date);
    });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Teknik Servis Yönetimi</h1>
          <p className="text-muted-foreground">Kurulum ve arıza kayıtlarını yönetin</p>
        </div>
        {canManage && (
          <Button onClick={() => openModal()} data-testid="add-service-btn">
            <Plus className="h-4 w-4 mr-2" />
            Yeni Kayıt
          </Button>
        )}
      </div>

      {/* İstatistikler */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Bekleyen</p>
                  <p className="text-xl font-bold">{stats.bekliyor}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Planlanan</p>
                  <p className="text-xl font-bold">{stats.planlandi}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-purple-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Devam Eden</p>
                  <p className="text-xl font-bold">{stats.devam_ediyor}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Tamamlanan</p>
                  <p className="text-xl font-bold">{stats.tamamlandi}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Toplam</p>
                  <p className="text-xl font-bold">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Görünüm Değiştir & Filtreler */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <FileText className="h-4 w-4 mr-1" />
            Tüm Kurulumlar
          </Button>
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('calendar')}
          >
            <Calendar className="h-4 w-4 mr-1" />
            Takvim
          </Button>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {/* Dönem Filtresi */}
          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="w-32">
              <Filter className="h-4 w-4 mr-1" />
              <SelectValue placeholder="Dönem" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="weekly">Bu Hafta</SelectItem>
              <SelectItem value="monthly">Bu Ay</SelectItem>
              <SelectItem value="yearly">Bu Yıl</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Müşteri ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-48"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Durum" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              <SelectItem value="bekliyor">Bekliyor</SelectItem>
              <SelectItem value="planlandi">Planlandı</SelectItem>
              <SelectItem value="devam_ediyor">Devam Ediyor</SelectItem>
              <SelectItem value="tamamlandi">Tamamlandı</SelectItem>
              <SelectItem value="iptal">İptal</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Liste Görünümü - Tüm Kurulumlar */}
      {viewMode === 'list' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sol: Tüm Kurulumlar Listesi */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  Tüm Kurulumlar ({filteredServices.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Müşteri</th>
                        <th className="text-left p-3 font-medium">Telefon</th>
                        <th className="text-left p-3 font-medium">Tarih</th>
                        <th className="text-left p-3 font-medium">Tutar</th>
                        <th className="text-left p-3 font-medium">Durum</th>
                        <th className="text-left p-3 font-medium">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={6} className="text-center p-8">
                            <div className="spinner h-8 w-8 mx-auto" />
                          </td>
                        </tr>
                      ) : filteredServices.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center p-8 text-muted-foreground">
                            Kayıt bulunamadı
                          </td>
                        </tr>
                      ) : (
                        filteredServices.map((service) => (
                          <tr key={service.id} className="border-b hover:bg-muted/30">
                            <td className="p-3">
                              <div className="font-medium">{service.customer_name}</div>
                              {service.customer_city && (
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {service.customer_city}
                                </div>
                              )}
                            </td>
                            <td className="p-3">
                              <a href={`tel:${service.customer_phone}`} className="flex items-center gap-1 text-primary hover:underline text-sm">
                                <Phone className="h-3 w-3" />
                                {service.customer_phone}
                              </a>
                            </td>
                            <td className="p-3 text-sm">
                              {service.planned_date ? (
                                <div>
                                  <div>{formatDate(service.planned_date)}</div>
                                  {service.planned_time && (
                                    <div className="text-xs text-muted-foreground">{service.planned_time}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="p-3 font-medium text-sm">{formatCurrency(service.sale_amount)}</td>
                            <td className="p-3">{renderStatusBadge(service.status)}</td>
                            <td className="p-3">
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openDetailModal(service)} title="Detay">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {canManage && (
                                  <>
                                    <Button variant="ghost" size="icon" onClick={() => openModal(service)} title="Düzenle">
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => handleDelete(service.id)} title="Sil">
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sağ: Gelecek Kurulumlar */}
          <div className="lg:col-span-1">
            <Card className="border-orange-200 dark:border-orange-800 sticky top-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-500" />
                  Gelecek Kurulumlar ({upcomingServices.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto">
                {upcomingServices.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">Planlanmış kurulum yok</p>
                ) : (
                  <div className="space-y-2">
                    {upcomingServices.map((service) => (
                      <div
                        key={service.id}
                        className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => openDetailModal(service)}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-sm">{service.customer_name}</span>
                          {renderStatusBadge(service.status)}
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {service.customer_phone}
                          </div>
                          {service.planned_date && (
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(service.planned_date)}
                              {service.planned_time && ` - ${service.planned_time}`}
                            </div>
                          )}
                          {service.customer_city && (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {service.customer_city}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Takvim Görünümü */}
      {viewMode === 'calendar' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={prevMonth}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">
                  {currentMonth.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
                </h2>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Bugün
                </Button>
              </div>
              <Button variant="ghost" size="icon" onClick={nextMonth}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Haftanın günleri */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Takvim günleri - Lacivert dolu, Sarı boş */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, idx) => (
                <div
                  key={idx}
                  onClick={() => day.isCurrentMonth && handleDateClick(day.dateStr, day.services)}
                  className={cn(
                    "min-h-[80px] p-1 border rounded-lg cursor-pointer transition-colors",
                    !day.isCurrentMonth && "bg-muted/30 opacity-50",
                    day.isCurrentMonth && day.hasServices && "bg-blue-900 text-white border-blue-800",
                    day.isCurrentMonth && !day.hasServices && "bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700",
                    day.isToday && "ring-2 ring-primary",
                    selectedDate === day.dateStr && "ring-2 ring-offset-2 ring-primary"
                  )}
                >
                  {day.day && (
                    <>
                      <div className={cn(
                        "text-sm font-medium mb-1",
                        day.hasServices && "text-white",
                        day.isToday && !day.hasServices && "text-primary"
                      )}>
                        {day.day}
                      </div>
                      {day.services && day.services.length > 0 && (
                        <div className="space-y-1">
                          {day.services.slice(0, 2).map((s, i) => (
                            <div
                              key={i}
                              onClick={(e) => { e.stopPropagation(); openDetailModal(s); }}
                              className="text-xs p-1 rounded truncate cursor-pointer bg-white/20 hover:bg-white/30"
                            >
                              {s.customer_name}
                            </div>
                          ))}
                          {day.services.length > 2 && (
                            <div className="text-xs text-center opacity-75">
                              +{day.services.length - 2} daha
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Yeni/Düzenle Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingService ? 'Kaydı Düzenle' : 'Yeni Teknik Servis Kaydı'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Müşteri Bilgileri */}
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-muted-foreground">Müşteri Bilgileri</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ad Soyad *</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label>Telefon *</Label>
                  <Input
                    value={formData.customer_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Adres</Label>
                <Textarea
                  value={formData.customer_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>İl</Label>
                  <Input
                    value={formData.customer_city}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_city: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>İlçe</Label>
                  <Input
                    value={formData.customer_district}
                    onChange={(e) => setFormData(prev => ({ ...prev, customer_district: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Satış Bilgileri & Ürün Seçimi */}
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-muted-foreground">Satış Bilgileri & Ürünler</h3>
              <div>
                <Label>Satış Tutarı (₺)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.sale_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, sale_amount: e.target.value }))}
                />
              </div>
              
              {/* Ürün Arama ve Seçme */}
              <div className="border rounded-lg p-3 space-y-3">
                <Label>Ürün Listesi</Label>
                
                {/* Ürün Arama */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Ürün ara ve ekle..."
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                  {productSearchTerm && filteredProducts.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {filteredProducts.map(product => (
                        <div
                          key={product.id}
                          className="p-2 hover:bg-muted cursor-pointer flex justify-between items-center"
                          onClick={() => addProductFromList(product)}
                        >
                          <span className="text-sm">{product.name}</span>
                          <span className="text-xs text-muted-foreground">{formatCurrency(product.sale_price_tl || product.price_tl)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Seçili Ürünler */}
                {formData.items.length > 0 && (
                  <div className="space-y-2">
                    {formData.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-muted/50 p-2 rounded text-sm">
                        <span className="flex-1 truncate">{item.product_name}</span>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItemQuantity(idx, e.target.value)}
                            className="w-16 h-8 text-center"
                          />
                          <span className="w-24 text-right">{formatCurrency(item.total_price)}</span>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Planlama */}
            <div className="space-y-3">
              <h3 className="font-medium text-sm text-muted-foreground">Planlama</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tarih</Label>
                  <Input
                    type="date"
                    value={formData.planned_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, planned_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Saat</Label>
                  <Input
                    type="time"
                    value={formData.planned_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, planned_time: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Durum</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bekliyor">Kurulum Bekliyor</SelectItem>
                    <SelectItem value="planlandi">Planlama Yapıldı</SelectItem>
                    <SelectItem value="devam_ediyor">Kurulum Devam Ediyor</SelectItem>
                    <SelectItem value="tamamlandi">Kurulum Tamamlandı</SelectItem>
                    <SelectItem value="iptal">İptal Edildi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Açıklama ve Notlar */}
            <div className="space-y-3">
              <div>
                <Label>Kurulum Açıklaması</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  placeholder="Kurulum ile ilgili genel açıklama..."
                />
              </div>
              <div>
                <Label>Notlar</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  placeholder="Müşteri ile ilgili notlar..."
                />
              </div>
              <div>
                <Label>İç Not (Sadece personel görür)</Label>
                <Textarea
                  value={formData.internal_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, internal_notes: e.target.value }))}
                  rows={2}
                  placeholder="Dahili notlar..."
                />
              </div>
            </div>

            {/* Arıza Kaydı */}
            <div className="space-y-3 border-t pt-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_repair"
                  checked={formData.is_repair}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_repair: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="is_repair" className="flex items-center gap-1 cursor-pointer">
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  Arıza Kaydı
                </Label>
              </div>
              {formData.is_repair && (
                <>
                  <div>
                    <Label>Arızalı Ürünler</Label>
                    <Input
                      value={formData.faulty_products}
                      onChange={(e) => setFormData(prev => ({ ...prev, faulty_products: e.target.value }))}
                      placeholder="Arızalı ürün adları..."
                    />
                  </div>
                  <div>
                    <Label>Arıza Açıklaması</Label>
                    <Textarea
                      value={formData.repair_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, repair_description: e.target.value }))}
                      rows={2}
                      placeholder="Arıza detayları..."
                    />
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                İptal
              </Button>
              <Button type="submit">
                {editingService ? 'Güncelle' : 'Kaydet'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detay Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Teknik Servis Detayı</DialogTitle>
          </DialogHeader>
          
          {selectedService && (
            <div className="space-y-4">
              {/* Müşteri Bilgileri */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <h3 className="font-semibold">{selectedService.customer_name}</h3>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4" />
                  <a href={`tel:${selectedService.customer_phone}`} className="text-primary hover:underline">
                    {selectedService.customer_phone}
                  </a>
                </div>
                {selectedService.customer_address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 mt-0.5" />
                    <span>
                      {selectedService.customer_address}
                      {selectedService.customer_city && `, ${selectedService.customer_city}`}
                      {selectedService.customer_district && ` / ${selectedService.customer_district}`}
                    </span>
                  </div>
                )}
              </div>

              {/* Durum ve Tutar */}
              <div className="flex items-center justify-between">
                <div>{renderStatusBadge(selectedService.status)}</div>
                <div className="text-xl font-bold">{formatCurrency(selectedService.sale_amount)}</div>
              </div>

              {/* Planlama */}
              {selectedService.planned_date && (
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(selectedService.planned_date)}
                  </div>
                  {selectedService.planned_time && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {selectedService.planned_time}
                    </div>
                  )}
                </div>
              )}

              {/* Açıklama */}
              {selectedService.description && (
                <div>
                  <h4 className="font-medium mb-1">Kurulum Açıklaması</h4>
                  <p className="text-sm text-muted-foreground">{selectedService.description}</p>
                </div>
              )}

              {/* Ürün Listesi */}
              {selectedService.items && selectedService.items.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium flex items-center gap-1">
                      <Package className="h-4 w-4" />
                      Ürünler
                    </h4>
                    <Button variant="outline" size="sm" onClick={() => generateProductListPdf(selectedService)}>
                      <Download className="h-4 w-4 mr-1" />
                      PDF İndir
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {selectedService.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm bg-muted/30 p-2 rounded">
                        <span>{item.product_name} x {item.quantity}</span>
                        <span>{formatCurrency(item.total_price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notlar */}
              {selectedService.notes && (
                <div>
                  <h4 className="font-medium mb-1">Notlar</h4>
                  <p className="text-sm text-muted-foreground">{selectedService.notes}</p>
                </div>
              )}

              {/* Arıza Bilgisi */}
              {selectedService.is_repair && (
                <div className="border border-orange-200 bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                  <h4 className="font-medium text-orange-700 dark:text-orange-400 flex items-center gap-1 mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    Arıza Kaydı
                  </h4>
                  {selectedService.faulty_products && (
                    <p className="text-sm"><strong>Arızalı Ürünler:</strong> {selectedService.faulty_products}</p>
                  )}
                  {selectedService.repair_description && (
                    <p className="text-sm"><strong>Açıklama:</strong> {selectedService.repair_description}</p>
                  )}
                </div>
              )}

              {/* Kurulum Fotoğrafları */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium flex items-center gap-1">
                    <Camera className="h-4 w-4" />
                    Kurulum Fotoğrafları
                  </h4>
                  {canManage && (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        disabled={uploadingPhotos}
                      />
                      <Button variant="outline" size="sm" disabled={uploadingPhotos} asChild>
                        <span>
                          {uploadingPhotos ? (
                            <div className="spinner h-4 w-4 mr-1" />
                          ) : (
                            <Plus className="h-4 w-4 mr-1" />
                          )}
                          Fotoğraf Ekle
                        </span>
                      </Button>
                    </label>
                  )}
                </div>
                
                {selectedService.installation_photos && selectedService.installation_photos.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {selectedService.installation_photos.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={photo}
                          alt={`Kurulum ${idx + 1}`}
                          className="w-full h-24 object-cover rounded-lg cursor-pointer"
                          onClick={() => window.open(photo, '_blank')}
                        />
                        {canManage && (
                          <button
                            onClick={() => handlePhotoDelete(idx)}
                            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Henüz fotoğraf eklenmemiş</p>
                )}
              </div>

              {/* Durum Güncelleme Butonları */}
              {canManage && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Durumu Güncelle</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <Button
                        key={key}
                        variant={selectedService.status === key ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => updateStatus(selectedService.id, key)}
                        disabled={selectedService.status === key}
                      >
                        <config.icon className="h-3 w-3 mr-1" />
                        {config.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter>
                {canManage && (
                  <>
                    <Button variant="destructive" onClick={() => handleDelete(selectedService.id)}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      Sil
                    </Button>
                    <Button variant="outline" onClick={() => { setIsDetailModalOpen(false); openModal(selectedService); }}>
                      <Pencil className="h-4 w-4 mr-1" />
                      Düzenle
                    </Button>
                  </>
                )}
                <Button onClick={() => setIsDetailModalOpen(false)}>Kapat</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TechnicalService;
